"use client";

import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface Props {
  content: string | null | undefined;
  expanded: boolean;
  onToggleExpanded: (next: boolean) => void;
  brandNoticeHtml?: string;
  brandNoticeTitle?: string | null;
  brandNoticeIsHtml?: boolean;
  value?: string;
  onValueChange?: (next: string) => void;
}

export function ProductInfoTabs({
  content,
  expanded,
  onToggleExpanded,
  brandNoticeHtml,
  brandNoticeTitle,
  brandNoticeIsHtml,
  value,
  onValueChange,
}: Props) {
  const hasBrandNotice = !!(brandNoticeHtml || brandNoticeTitle);
  const tabLabels = [
    "상품정보",
    ...(hasBrandNotice ? ["판매자 공지"] : []),
    "배송안내",
    "교환/반품",
  ];

  return (
    <Tabs defaultValue="상품정보" value={value} onValueChange={onValueChange}>
      <TabsList className="w-full justify-start border-b border-border rounded-none bg-transparent p-0 h-auto">
        {tabLabels.map((label) => (
          <TabsTrigger
            key={label}
            value={label}
            className="rounded-none border-b-2 border-transparent text-muted-foreground hover:text-foreground hover:border-neutral-300 data-[state=active]:text-foreground data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none px-5 py-3 text-[13px] transition-colors"
          >
            <span className="relative inline-flex items-center">
              {label}
              {label === "판매자 공지" && (
                <span
                  aria-hidden
                  className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-red-500"
                />
              )}
            </span>
          </TabsTrigger>
        ))}
      </TabsList>

      <TabsContent value="상품정보" className="pt-6">
        {content ? (
          <div className="relative">
            <div
              className={cn(
                "overflow-hidden transition-[max-height] duration-500",
                expanded ? "max-h-none" : "max-h-[600px]",
              )}
            >
              <div
                className="prose prose-sm max-w-none mx-auto [&_img]:mx-auto [&_img]:block [&_img]:max-w-full"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            </div>
            {!expanded ? (
              <div className="absolute bottom-0 left-0 right-0 pt-24 bg-gradient-to-t from-background via-background/80 to-transparent pb-0">
                <button
                  type="button"
                  className="w-full border border-border bg-background/90 backdrop-blur-sm py-3.5 text-[13px] text-muted-foreground hover:text-foreground hover:border-foreground transition-colors flex items-center justify-center gap-1.5 rounded-sm"
                  onClick={() => onToggleExpanded(true)}
                >
                  상품설명 더보기
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  className="w-full border border-border py-3.5 text-[13px] text-muted-foreground hover:text-foreground hover:border-foreground transition-colors flex items-center justify-center gap-1.5 rounded-sm"
                  onClick={() => onToggleExpanded(false)}
                >
                  상품설명 접기
                  <ChevronDown className="h-3.5 w-3.5 rotate-180" />
                </button>
              </div>
            )}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm py-8 text-center">상품 정보가 없습니다.</p>
        )}
      </TabsContent>

      {hasBrandNotice && (
        <TabsContent value="판매자 공지" className="pt-6">
          <div className="max-w-2xl mx-auto px-4">
            {brandNoticeTitle && (
              <p className="text-[14px] font-medium text-foreground mb-4 text-center">
                {brandNoticeTitle}
              </p>
            )}
            {brandNoticeHtml &&
              (brandNoticeIsHtml ? (
                <div
                  className="prose prose-sm max-w-none text-[13px] text-muted-foreground leading-relaxed text-center [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_h1]:text-base [&_h1]:font-semibold [&_h2]:text-sm [&_h2]:font-semibold [&_img]:block [&_img]:mx-auto [&_img]:my-3 [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded"
                  dangerouslySetInnerHTML={{ __html: brandNoticeHtml }}
                />
              ) : (
                <div
                  className="text-[13px] text-muted-foreground leading-relaxed whitespace-pre-line break-words [&_img]:block [&_img]:mx-auto [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded [&_img]:my-3"
                  dangerouslySetInnerHTML={{ __html: brandNoticeHtml }}
                />
              ))}
          </div>
        </TabsContent>
      )}

      <TabsContent value="배송안내" className="py-6">
        <div className="space-y-3 text-[13px] text-muted-foreground">
          <p>배송비: 브랜드별 상이 (상품 페이지 참고)</p>
          <p>배송 기간: 결제 확인 후 2~5 영업일 이내 출고</p>
          <p>도매 배송은 브랜드에서 직접 발송됩니다.</p>
        </div>
      </TabsContent>

      <TabsContent value="교환/반품" className="py-6">
        <div className="space-y-3 text-[13px] text-muted-foreground">
          <p>교환/반품 신청: 상품 수령 후 7일 이내</p>
          <p>교환/반품 배송비: 고객 부담 (단순 변심 시)</p>
          <p>불량 상품의 경우 브랜드에서 배송비를 부담합니다.</p>
          <p>주문 제작 상품은 교환/반품이 불가합니다.</p>
        </div>
      </TabsContent>
    </Tabs>
  );
}
